import React, { useEffect, useState } from "react";

import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { toast } from "react-toastify";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { getContactDisplayName } from "../../helpers/contactDisplayName";

const createRequestKey = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `os-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

const localDate = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
};

const localTime = (offsetHours = 0) => {
  const value = new Date(Date.now() + offsetHours * 60 * 60 * 1000);
  return `${String(value.getHours()).padStart(2, "0")}:${String(value.getMinutes()).padStart(2, "0")}`;
};

const GnsOsServiceOrderModal = ({ modalOpen, onClose, ticket }) => {
  const displayName = getContactDisplayName(ticket.contact);
  const [clients, setClients] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [links, setLinks] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedTechnician, setSelectedTechnician] = useState(null);
  const [clientFinal, setClientFinal] = useState("");
  const [serviceType, setServiceType] = useState("asistencia");
  const [assistanceType, setAssistanceType] = useState("remoto");
  const [serviceDate, setServiceDate] = useState(localDate());
  const [startTime, setStartTime] = useState(localTime());
  const [endTime, setEndTime] = useState(localTime(1));
  const [description, setDescription] = useState("");
  const [workDescription, setWorkDescription] = useState("");
  const [accountContract, setAccountContract] = useState(true);
  const [notifyContact, setNotifyContact] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingClients, setLoadingClients] = useState(false);
  const [requestKey, setRequestKey] = useState(createRequestKey);

  useEffect(() => {
    if (!modalOpen || !ticket.id) return;

    const load = async () => {
      setLoadingClients(true);
      try {
        const [clientsResponse, techniciansResponse, linksResponse] = await Promise.all([
          api.get("/tickets/gns-os/clients"),
          api.get("/tickets/gns-os/technicians"),
          api.get(`/tickets/${ticket.id}/gns-os-service-orders`),
        ]);
        setClients(clientsResponse.data.clients || []);
        setTechnicians(techniciansResponse.data.technicians || []);
        setLinks(linksResponse.data.links || []);
        setClientFinal((current) => current || displayName || "");
      } catch (err) {
        toastError(err);
      } finally {
        setLoadingClients(false);
      }
    };

    load();
  }, [modalOpen, ticket.id, displayName]);

  const handleClose = () => {
    setSelectedClient(null);
    setSelectedTechnician(null);
    setClientFinal("");
    setServiceType("asistencia");
    setAssistanceType("remoto");
    setServiceDate(localDate());
    setStartTime(localTime());
    setEndTime(localTime(1));
    setDescription("");
    setWorkDescription("");
    setAccountContract(true);
    setNotifyContact(true);
    setLinks([]);
    setRequestKey(createRequestKey());
    onClose();
  };

  const handleClientChange = (_event, client) => {
    setSelectedClient(client);
    if (client?.customerType === "occasional") setAccountContract(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedClient) return;

    setLoading(true);
    try {
      const { data } = await api.post(`/tickets/${ticket.id}/gns-os-service-orders`, {
        gnsOsClientId: selectedClient.id,
        clientFinal,
        serviceType,
        assistanceType: serviceType === "asistencia" ? assistanceType : "",
        serviceDate,
        startTime,
        endTime,
        description,
        workDescription,
        technicianId: selectedTechnician?.id || null,
        accountContract,
        notifyContact,
        requestKey,
      });
      setLinks((current) => [data.link, ...current]);
      setDescription("");
      setWorkDescription("");
      setRequestKey(createRequestKey());
      if (notifyContact && !data.notificationSent) {
        toast.warn(`O.S. #${data.link.gnsOsOrderNumber} registrada, pero WhatsApp no pudo enviar la confirmacion.`);
      } else {
        toast.success(`O.S. #${data.link.gnsOsOrderNumber} registrada`);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={modalOpen} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">
      <form onSubmit={handleSubmit}>
        <DialogTitle>Registrar orden de servicio en GNS-OS</DialogTitle>
        <DialogContent dividers>
          {links.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <strong>Ordenes registradas en esta conversacion</strong>
              {links.map((item) => (
                <TextField
                  key={item.id}
                  fullWidth
                  variant="outlined"
                  label="Orden de servicio"
                  value={`#${item.gnsOsOrderNumber}`}
                  InputProps={{ readOnly: true }}
                  style={{ marginTop: 10 }}
                />
              ))}
            </div>
          )}
          <Autocomplete
            options={clients}
            value={selectedClient}
            onChange={handleClientChange}
            getOptionLabel={(option) => option.nombre || ""}
            loading={loadingClients}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Cliente de GNS-OS"
                variant="outlined"
                required
                autoFocus
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingClients ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <TextField
            fullWidth
            variant="outlined"
            label="Contacto visible en la O.S."
            value={clientFinal}
            onChange={(event) => setClientFinal(event.target.value)}
            inputProps={{ maxLength: 180 }}
            style={{ marginTop: 12 }}
          />
          <Autocomplete
            options={technicians}
            value={selectedTechnician}
            onChange={(_event, technician) => setSelectedTechnician(technician)}
            getOptionLabel={(option) => option.name || ""}
            loading={loadingClients}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tecnico asignado"
                variant="outlined"
                placeholder="Sin asignar"
                style={{ marginTop: 12 }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingClients ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel id="service-type-label">Tipo de servicio</InputLabel>
              <Select
                labelId="service-type-label"
                label="Tipo de servicio"
                value={serviceType}
                onChange={(event) => setServiceType(event.target.value)}
              >
                <MenuItem value="asistencia">Asistencia informatica</MenuItem>
                <MenuItem value="capacitacion">Capacitacion</MenuItem>
                <MenuItem value="entrega">Entrega de equipo</MenuItem>
                <MenuItem value="retiro">Retiro de equipo</MenuItem>
                <MenuItem value="otro">Otro</MenuItem>
              </Select>
            </FormControl>
            {serviceType === "asistencia" && (
              <FormControl variant="outlined" fullWidth>
                <InputLabel id="assistance-type-label">Modalidad</InputLabel>
                <Select
                  labelId="assistance-type-label"
                  label="Modalidad"
                  value={assistanceType}
                  onChange={(event) => setAssistanceType(event.target.value)}
                >
                  <MenuItem value="remoto">Remoto</MenuItem>
                  <MenuItem value="presencial">Presencial</MenuItem>
                  <MenuItem value="telefonico">Telefonico</MenuItem>
                </Select>
              </FormControl>
            )}
          </div>
          <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
            <TextField
              fullWidth
              required
              type="date"
              variant="outlined"
              label="Fecha"
              value={serviceDate}
              onChange={(event) => setServiceDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              required
              type="time"
              variant="outlined"
              label="Inicio"
              value={startTime}
              onChange={(event) => setStartTime(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              required
              type="time"
              variant="outlined"
              label="Fin"
              value={endTime}
              onChange={(event) => setEndTime(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </div>
          <TextField
            fullWidth
            required
            multiline
            rows={4}
            variant="outlined"
            label="Detalle para la O.S. y el cliente"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            inputProps={{ maxLength: 3000 }}
            style={{ marginTop: 12 }}
          />
          <TextField
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            label="Trabajo realizado"
            value={workDescription}
            onChange={(event) => setWorkDescription(event.target.value)}
            inputProps={{ maxLength: 3000 }}
            style={{ marginTop: 12 }}
          />
          <FormControlLabel
            control={<Checkbox checked={accountContract} onChange={(event) => setAccountContract(event.target.checked)} color="primary" />}
            label="Descontar el tiempo de horas contratadas"
            style={{ marginTop: 8 }}
          />
          <FormControlLabel
            control={<Checkbox checked={notifyContact} onChange={(event) => setNotifyContact(event.target.checked)} color="primary" />}
            label="Enviar confirmacion y enlace de la O.S. por WhatsApp"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="default" disabled={loading}>Cancelar</Button>
          <ButtonWithSpinner type="submit" color="primary" variant="contained" loading={loading} disabled={!selectedClient}>
            Registrar O.S.
          </ButtonWithSpinner>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default GnsOsServiceOrderModal;
